import * as React from 'react';
import { Mic, Loader2, Copy, Check } from 'lucide-react';

import { Button } from 'components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from 'components/ui/dialog';
import { AudioRecorder } from 'components/ui/audio-recorder';
import { useTranscribeAudio } from 'features/transcription/api';
import { cn } from 'lib/utils';
import { Textarea } from 'components/ui/textarea';
import { Label } from 'components/ui/label';

export type InsertAction = 'insert' | 'replace' | 'copy';

export interface AudioInputDialogProps {
    onTranscriptionComplete: (text: string, action: InsertAction) => void;
    postProcessing?: string | null;
    allowCustomPostProcessing?: boolean;
    disabled?: boolean;
    className?: string;
    triggerLabel?: string;
}

export const AudioInputDialog = React.forwardRef<
    HTMLButtonElement,
    AudioInputDialogProps
>(
    (
        {
            onTranscriptionComplete,
            postProcessing = null,
            allowCustomPostProcessing = false,
            disabled = false,
            className,
            triggerLabel,
        },
        ref,
    ) => {
        const [open, setOpen] = React.useState(false);
        const [transcription, setTranscription] = React.useState<string | null>(
            null,
        );
        const [
            transcriptionBeforeProcessing,
            setTranscriptionBeforeProcessing,
        ] = React.useState<string | null>(null);
        const [customPostProcessing, setCustomPostProcessing] = React.useState<
            string | undefined
        >(undefined);
        const [copied, setCopied] = React.useState(false);
        const [recorderKey, setRecorderKey] = React.useState(0);

        const { mutate: transcribe, isPending } = useTranscribeAudio({
            mutationConfig: {
                onSuccess: (data) => {
                    setTranscription(data.transcription);
                    setTranscriptionBeforeProcessing(
                        data.pre_processed_transcription,
                    );
                },
                onError: (error) => {
                    console.error('Transcription error:', error);
                },
            },
        });

        const handleRecordingComplete = (audioBlob: Blob) => {
            const processingInstruction =
                allowCustomPostProcessing && customPostProcessing
                    ? customPostProcessing
                    : postProcessing;

            transcribe({
                audio: audioBlob,
                post_processing: processingInstruction,
            });
        };

        const handleAction = (action: InsertAction) => {
            if (!transcription) return;

            if (action === 'copy') {
                navigator.clipboard.writeText(transcription);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } else {
                onTranscriptionComplete(transcription, action);
                handleClose();
            }
        };

        const handleActionForTranscriptionBeforeProcessing = (
            action: InsertAction,
        ) => {
            if (!transcriptionBeforeProcessing) return;

            if (action === 'copy') {
                navigator.clipboard.writeText(transcriptionBeforeProcessing);
            }
        };

        const handleClose = () => {
            setOpen(false);
            setTranscription(null);
            setCustomPostProcessing('');
            setCopied(false);
            // Force remount of AudioRecorder to clean up resources
            setRecorderKey((prev) => prev + 1);
        };

        const handleOpenChange = (newOpen: boolean) => {
            if (!newOpen && !isPending) {
                handleClose();
            } else {
                setOpen(newOpen);
            }
        };

        return (
            <Dialog open={open} onOpenChange={handleOpenChange}>
                <DialogTrigger asChild>
                    <Button
                        ref={ref}
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={cn('h-8 w-8', className)}
                        disabled={disabled}
                        title={triggerLabel || 'Voice input'}
                    >
                        <Mic className="h-4 w-4" />
                    </Button>
                </DialogTrigger>
                <DialogContent
                    className="max-w-2xl"
                    onInteractOutside={(e) => {
                        if (isPending) {
                            e.preventDefault();
                        }
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>Voice Input</DialogTitle>
                        <DialogDescription>
                            Record your message and choose how to insert it
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4">
                        {!transcription && !isPending && (
                            <>
                                {allowCustomPostProcessing && (
                                    <div className="space-y-2">
                                        <Label htmlFor="post-processing">
                                            Post-processing instructions
                                            (optional)
                                        </Label>
                                        <Textarea
                                            id="post-processing"
                                            placeholder="e.g., Format as bullet points, clean up filler words..."
                                            value={customPostProcessing}
                                            onChange={(e) =>
                                                setCustomPostProcessing(
                                                    e.target.value,
                                                )
                                            }
                                            enableAudio={true}
                                        />
                                        <p className="text-xs text-muted-foreground">
                                            Describe how the transcription
                                            should be processed after recording
                                        </p>
                                    </div>
                                )}

                                <AudioRecorder
                                    key={recorderKey}
                                    onRecordingComplete={
                                        handleRecordingComplete
                                    }
                                    onCancel={handleClose}
                                />
                            </>
                        )}

                        {isPending && (
                            <div className="flex flex-col items-center justify-center space-y-3 py-12">
                                <Loader2 className="h-12 w-12 animate-spin text-primary" />
                                <p className="text-sm text-muted-foreground">
                                    Transcribing your audio...
                                </p>
                                {(allowCustomPostProcessing &&
                                    customPostProcessing) ||
                                postProcessing ? (
                                    <p className="text-xs text-muted-foreground">
                                        Applying post-processing...
                                    </p>
                                ) : null}
                            </div>
                        )}

                        {transcription && !isPending && (
                            <div className="space-y-3">
                                <div className="space-y-2">
                                    <Label>Transcription Result</Label>
                                    <Textarea
                                        value={transcription}
                                        onChange={(e) =>
                                            setTranscription(e.target.value)
                                        }
                                        className="min-h-[200px] resize-none"
                                        placeholder="Your transcription will appear here..."
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        You can edit the transcription before
                                        inserting it
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {transcription && !isPending && (
                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleAction('copy')}
                                className="gap-2"
                            >
                                {copied ? (
                                    <>
                                        <Check className="h-4 w-4" />
                                        Copied
                                    </>
                                ) : (
                                    <>
                                        <Copy className="h-4 w-4" />
                                        Copy
                                    </>
                                )}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => handleAction('replace')}
                            >
                                Replace
                            </Button>
                            <Button
                                type="button"
                                onClick={() => handleAction('insert')}
                            >
                                Insert
                            </Button>
                        </DialogFooter>
                    )}

                    {transcriptionBeforeProcessing && !isPending && (
                        <div className="space-y-3">
                            <div className="space-y-2">
                                <Label>Raw Transcription</Label>
                                <Textarea
                                    value={transcriptionBeforeProcessing}
                                    onChange={(e) =>
                                        setTranscriptionBeforeProcessing(
                                            e.target.value,
                                        )
                                    }
                                    className="min-h-[200px] resize-none"
                                    placeholder="Your transcription will appear here..."
                                />
                            </div>
                        </div>
                    )}

                    {transcription && !isPending && (
                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    handleActionForTranscriptionBeforeProcessing(
                                        'copy',
                                    )
                                }
                                className="gap-2"
                            >
                                <>
                                    <Copy className="h-4 w-4" />
                                    Copy
                                </>
                            </Button>
                        </DialogFooter>
                    )}
                </DialogContent>
            </Dialog>
        );
    },
);

AudioInputDialog.displayName = 'AudioInputDialog';
